import React, { useId, useRef, useState } from 'react';
import { docsVisuals } from '@/content/docsVisuals';
import './DocsVisual.css';

// Screenshots stay untouched. Callouts are real, accessible controls layered
// over the capture, so text stays sharp and readable at every viewport size.
export default function DocsVisual({ visual, priority = false, tour = false }) {
  const entry = docsVisuals[visual];
  const [active, setActive] = useState(0);
  const [failed, setFailed] = useState(false);
  const [actualSize, setActualSize] = useState(false);
  const dialog = useRef(null);
  const id = useId();
  if (!entry) return null;

  const { title, caption, image, width, height, alt, steps } = entry;
  const src = `/docs/screenshots/${image}`;
  const area = steps[active]?.area;
  const position = ([x, y, w, h]) => ({ left: `${x}%`, top: `${y}%`, width: `${w}%`, height: `${h}%` });

  return (
    <figure className={`docs-visual ${height > width ? 'docs-visual--portrait' : ''} ${tour ? 'docs-visual--tour' : ''}`} aria-labelledby={`${id}-title`}>
      <figcaption className="docs-visual-heading">
        <span className="docs-visual-eyebrow">A closer look</span>
        <h3 id={`${id}-title`}>{title}</h3>
        <p>{caption}</p>
      </figcaption>
      <div className="docs-visual-body">
        <div className="docs-visual-stage">
          {failed ? <p className="docs-visual-fallback">The screenshot could not load. Follow the numbered instructions below.</p> : (
            <div className="docs-visual-capture" style={{ aspectRatio: `${width} / ${height}` }}>
              <img src={src} width={width} height={height} alt={alt} loading={priority ? 'eager' : 'lazy'} decoding="async" onError={() => setFailed(true)} />
              {area && <span aria-hidden="true" className="docs-visual-highlight" style={position(area)} />}
              {steps.map((step, index) => (
                <button key={step.title} type="button" className="docs-visual-pin" style={{ left: `${step.pin[0]}%`, top: `${step.pin[1]}%` }} aria-label={`Highlight ${index + 1}: ${step.title}`} aria-pressed={active === index} onClick={() => setActive(index)}>{index + 1}</button>
              ))}
            </div>
          )}
          {!failed && <button type="button" className="docs-visual-enlarge" onClick={() => { setActualSize(false); dialog.current?.showModal(); }} aria-label={`Enlarge image: ${title}`}><span aria-hidden="true">⤢</span> Enlarge image</button>}
        </div>
        <ol className="docs-visual-steps" aria-label={`${title}: controls explained`}>
          {steps.map((step, index) => (
            <li key={step.title}>
              <button type="button" className="docs-visual-step" aria-pressed={active === index} onClick={() => setActive(index)}>
                <span className="docs-visual-number" aria-hidden="true">{index + 1}</span>
                <span><strong>{step.title}</strong><span className="docs-visual-description">{step.copy}</span></span>
              </button>
            </li>
          ))}
        </ol>
      </div>
      <dialog ref={dialog} className="docs-visual-dialog" aria-label={title} onClick={event => { if (event.target === dialog.current) dialog.current.close(); }}>
        <div className="docs-visual-dialog-header"><strong>{title}</strong><button type="button" onClick={() => dialog.current.close()} autoFocus aria-label="Close enlarged image">Close <span aria-hidden="true">×</span></button></div>
        <div className="docs-visual-dialog-toolbar"><p>{actualSize ? 'Scroll to explore the image at full size.' : 'The complete screenshot, fitted to your screen.'}</p><button type="button" aria-pressed={actualSize} onClick={() => setActualSize(value => !value)}>{actualSize ? 'Fit to window' : 'View actual size'}</button></div>
        <div className={`docs-visual-dialog-scroll ${actualSize ? 'docs-visual-dialog-scroll--actual' : ''}`}><img src={src} width={width} height={height} alt={alt} loading="lazy" /></div>
      </dialog>
    </figure>
  );
}

export function DocsWorkflow({ steps, title = 'From your idea to a finished file' }) {
  return <section className="docs-workflow" aria-label={title}>
    <span className="docs-visual-eyebrow">The workflow</span><h3>{title}</h3>
    <ol>{steps.map((step, index) => <li key={step.title}><span className="docs-workflow-index">{String(index + 1).padStart(2, '0')}</span><strong>{step.title}</strong><p>{step.copy}</p>{index < steps.length - 1 && <span className="docs-workflow-arrow" aria-hidden="true">→</span>}</li>)}</ol>
  </section>;
}
